import React, { useState, useCallback } from 'react';
import { Box, LinearProgress, IconButton, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useProgress } from '../../contexts/ProgressContext';
import { getPartDetails, getAllPartsForUsecase, getWorkstationDetails, getUsecaseDetails } from '../../services/syntheticDataset';
import { livisAlert } from '../../helpers/alert';
import { FloatingStackPortal } from '../common/FloatingStackPortal';
import '../../assets/css/common_styles.scss';

interface ProgressWidgetProps {
    onMaximize?: () => void;
}

const ProgressWidget: React.FC<ProgressWidgetProps> = ({ onMaximize }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [openInProgress, setOpenInProgress] = useState(false);
    const [showFetchProgress, setShowFetchProgress] = useState('');

    const isManageDataPage = location.pathname.includes('/data/');
    const currentPartId = isManageDataPage ? (location.pathname.match(/\/data\/([^/]+)/)?.[1] ?? undefined) : undefined;

    const {
        progressItems,
        isMinimized,
        setIsMinimized,
        isModalOpen,
        setIsModalOpen,
        clearCompleted
    } = useProgress();

    const {
        completedCount, errorCount, totalCount, overallProgress, overallStatus,
        partName, uploadingPartId, isInProcessingPhase
    } = React.useMemo(() => {
        const itemsList = Object.values(progressItems || {});
        const total = itemsList.length;
        const completed = itemsList.filter(item => item?.status === 'completed').length;
        const error = itemsList.filter(item => item?.status === 'error').length;

        const overall = total > 0
            ? Math.round((itemsList.reduce((sum, item) => sum + (item?.progress || 0), 0) / total))
            : 0;

        const hasActive = itemsList.some(item =>
            item?.status === 'uploading' || item?.status === 'processing' || item?.status === 'initiated'
        );

        const status = completed === total ? 'completed'
            : error > 0 ? 'error'
                : hasActive ? 'uploading'
                    : 'processing';

        const pName = itemsList.length > 0 ? itemsList[0]?.metadata?.partName : undefined;
        const pId = itemsList.length > 0 ? itemsList[0]?.metadata?.partId : undefined;

        // Processing phase: upload at 100%, at least one item still 'processing', not all completed
        const hasAnyProcessing = itemsList.some(item => item?.status === 'processing');
        const inProcessingPhase = total > 0 && overall >= 100 && hasAnyProcessing && status !== 'completed' && status !== 'error';

        return {
            completedCount: completed,
            errorCount: error,
            totalCount: total,
            overallProgress: overall,
            overallStatus: status,
            partName: pName,
            uploadingPartId: pId,
            isInProcessingPhase: inProcessingPhase
        };
    }, [progressItems]);

    const showOpen = uploadingPartId != null && (!isManageDataPage || currentPartId !== uploadingPartId);
    const isUploadPhaseActive = !isInProcessingPhase && overallStatus !== 'completed' && overallStatus !== 'error';

    const handleOpen = useCallback(async () => {
        if (!uploadingPartId || openInProgress) return;
        setOpenInProgress(true);
        try {
            setShowFetchProgress('Fetching Part Details');
            const partDetailsResponse = await getPartDetails(uploadingPartId);
            sessionStorage.setItem('part_data', JSON.stringify(partDetailsResponse.data.data));

            const usecaseId = partDetailsResponse.data.data?.usecase_id;
            if (!usecaseId) throw new Error('Usecase ID not found');

            const allPartsResponse = await getAllPartsForUsecase(usecaseId);
            sessionStorage.setItem('part_list', JSON.stringify(allPartsResponse.data.data?.parts ?? allPartsResponse.data));

            const workstationId = partDetailsResponse.data.data?.workstation_id;
            if (!workstationId) throw new Error('Workstation ID not found');

            setShowFetchProgress('Fetching Station Details');
            const workstationDetailsResponse = await getWorkstationDetails(workstationId);
            sessionStorage.setItem('station_data', JSON.stringify(workstationDetailsResponse.data.data));

            setShowFetchProgress('Fetching Usecase Details');
            const usecaseDetailsResponse = await getUsecaseDetails(usecaseId);
            sessionStorage.setItem('usecase_data', JSON.stringify(usecaseDetailsResponse.data.data));

            clearCompleted();
            navigate(`/app/part/data/${uploadingPartId}`);
        } catch (error) {
            console.error('Error navigating to data management:', error);
            livisAlert('Failed to complete data management process', 'error');
        } finally {
            setOpenInProgress(false);
            setShowFetchProgress('Data Fetched');
        }
    }, [uploadingPartId, openInProgress, clearCompleted, navigate, t]);

    if (totalCount === 0 || isModalOpen) return null;

    const getStatusText = (status: string): string => {
        switch (status) {
            case 'initiated': return t("Initiating...");
            case 'uploading': return t("Uploading");
            case 'processing': return t("Processing");
            case 'completed': return t("Completed");
            case 'error': return t("Failed");
            case 'interrupted': return t("Interrupted");
            case 'resumed':
            case 'resuming': return t("Resuming");
            default: return status || t("Unknown");
        }
    };

    const handleMaximize = () => {
        setIsModalOpen(true);
        if (onMaximize) onMaximize();
    };

    const toggleMinimized = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsMinimized(!isMinimized);
    };

    const uploadBarColor = overallStatus === 'completed' ? 'success' : overallStatus === 'error' ? 'error' : 'primary';
    const processingBarColor = overallStatus === 'completed' ? 'success' : overallStatus === 'error' ? 'error' : 'primary';

    const uploadBarClass = overallStatus === 'completed'
        ? 'livis-progress-widget-progress-bar livis-progress-widget-progress-bar-completed'
        : overallStatus === 'error'
            ? 'livis-progress-widget-progress-bar livis-progress-widget-progress-bar-error'
            : 'livis-progress-widget-progress-bar';

    return (
        <FloatingStackPortal order={1}>
        <Box className="livis-progress-widget">
            <Box className="livis-progress-widget-header">
                <span className="livis-progress-widget-title">
                    {t('Upload progress')} ({completedCount}/{totalCount})
                </span>
                <IconButton
                    size="small"
                    onClick={toggleMinimized}
                    className="livis-progress-widget-toggle-btn"
                    aria-label={isMinimized ? (t('Expand') as string) : (t('Collapse') as string)}
                >
                    {isMinimized ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Box>

            <Collapse in={!isMinimized}>
                {/* Upload phase: show progress and percentage */}
                {(isUploadPhaseActive || overallStatus === 'completed' || overallStatus === 'error') && !isInProcessingPhase && (
                    <>
                        <Box className="livis-progress-widget-row">
                            <CloudUploadOutlinedIcon className="livis-progress-widget-icon" />
                            <span className="livis-progress-widget-status">
                                {openInProgress ? (
                                    t(showFetchProgress)
                                ) : (
                                    <>
                                        {getStatusText(overallStatus)}
                                        {partName ? ` - ${partName}` : ''}
                                    </>
                                )}
                            </span>
                            <span className="livis-progress-widget-percentage">
                                {overallProgress}%
                            </span>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            color={uploadBarColor}
                            value={overallProgress}
                            className={uploadBarClass}
                        />
                    </>
                )}

                {/* Processing phase: indeterminate bar, "Processing files", no percentage */}
                {isInProcessingPhase && (
                    <>
                        <Box className="livis-progress-widget-row">
                            <SyncIcon className="livis-progress-widget-icon livis-progress-widget-icon-spin" />
                            <span className="livis-progress-widget-status">
                                {t("Processing files")}
                                {partName ? ` - ${partName}` : ''}
                            </span>
                        </Box>
                        <LinearProgress
                            variant="indeterminate"
                            color={processingBarColor}
                            className="livis-progress-widget-progress-bar"
                        />
                    </>
                )}

                <Box className="livis-progress-widget-actions">
                    <Box className="livis-progress-widget-actions-inner">
                        {overallStatus === 'completed' ? (
                            <>
                                <a
                                    href="#"
                                    className="livis-progress-widget-link livis-progress-widget-link-secondary"
                                    onClick={(e) => { e.preventDefault(); clearCompleted(); }}
                                >
                                    {t('Close')}
                                </a>
                                {showOpen && (
                                    <a
                                        href="#"
                                        className="livis-progress-widget-link"
                                        onClick={() => { handleOpen(); }}
                                        style={{ pointerEvents: openInProgress ? 'none' : undefined, opacity: openInProgress ? 0.7 : 1 }}
                                    >
                                        {openInProgress ? t('Fetching') + '...' : t('Show')}
                                    </a>
                                )}
                            </>
                        ) : overallStatus === 'error' ? (
                            <>
                                <a
                                    href="#"
                                    className="livis-progress-widget-link livis-progress-widget-link-secondary"
                                    onClick={(e) => { e.preventDefault(); clearCompleted(); }}
                                >
                                    {t('Dismiss')}
                                </a>
                                {showOpen && (
                                    <a
                                        href="#"
                                        className="livis-progress-widget-link"
                                        onClick={() => { handleOpen(); }}
                                        style={{ pointerEvents: openInProgress ? 'none' : undefined, opacity: openInProgress ? 0.7 : 1 }}
                                    >
                                        {openInProgress ? t('Fetching') + '...' : t('Show')}
                                    </a>
                                )}
                            </>
                        ) : (
                            <>
                                {(showOpen || (isManageDataPage && currentPartId != null && uploadingPartId != null && currentPartId === uploadingPartId)) && (
                                    <a
                                        href="#"
                                        className={`livis-progress-widget-link${showOpen ? ' livis-progress-widget-link-secondary' : ''}`}
                                        onClick={(e) => { e.preventDefault(); handleMaximize(); }}
                                    >
                                        {t('Maximize')}
                                    </a>
                                )}
                                {showOpen && (
                                    <a
                                        href="#"
                                        className="livis-progress-widget-link"
                                        onClick={(e) => { e.preventDefault(); handleOpen(); }}
                                        style={{ pointerEvents: openInProgress ? 'none' : undefined, opacity: openInProgress ? 0.7 : 1 }}
                                    >
                                        {openInProgress ? t('Showing...') : t('Show')}
                                    </a>
                                )}
                            </>
                        )}
                    </Box>
                </Box>
                {errorCount > 0 && (
                    <span className="livis-progress-widget-error">
                        {errorCount === 1
                            ? t("{{count}} file failed", { count: errorCount })
                            : t("{{count}} files failed", { count: errorCount })}
                    </span>
                )}
            </Collapse>
        </Box>
        </FloatingStackPortal>
    );
};

export default ProgressWidget;